import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Form,
  Button,
  InputGroup,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { showSuccess, showError } from "../../../components/toasts";
import { loginUser, resetAuthState } from "../../../redux/slices/authSlice";
import { useFacebookLogin } from "../../../hooks/useFacebookLogin";
import { useAppleLogin } from "../../../hooks/useAppleLogin";
import SEO from "../../../components/SEO/SEO";
import { seoConfig } from "../../../utils/seoConfig";
import { useTheme } from "../../../context/ThemeContext";
import { isDemoSite, DEMO_CREDENTIALS } from "../../../utils/environment";

import { ReactComponent as Logo } from "../../../assets/images/logo.svg";
import { ReactComponent as LogoDark } from "../../../assets/images/logo-dark.svg";

import OtpModal from "./OtpModal";

import { ReactComponent as Facebook } from "../../../assets/images/facebook.svg";
import { ReactComponent as Google } from "../../../assets/images/google.svg";
import { ReactComponent as Instagram } from "../../../assets/images/instagram.svg";
import { Apple } from "lucide-react";

import { ReactComponent as AppIcon } from "../../../assets/images/app-icon.svg";
import { ReactComponent as AndroindIcon } from "../../../assets/images/android-icon.svg";
import { ReactComponent as AppleIcon } from "../../../assets/images/apple_logo.svg";
import { ReactComponent as AppIconDark } from "../../../assets/images/apple-icon-dark.svg";
import { ReactComponent as AndroindIconDark } from "../../../assets/images/android-icon-dark.svg";

import { ReactComponent as EyeOff } from "../../../assets/images/eyeOff-icon.svg";
import { ReactComponent as Eye } from "../../../assets/images/eye-icon.svg";
import { getToken } from "../../../utils/auth";
import UseGoogleLogin from "../../../hooks/useGoogleLogin";
import UseInstagramLogin from "../../../hooks/useInstaLogin";
import VerifyOtpModal from "../../../pages/registration-steps/veryfy-otpmodal";
import Swal from "sweetalert2";

// Yup validation schema for login form
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const Login = () => {
  const { resolvedTheme } = useTheme();
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showOtpModals, setShowOtpModals] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const { hash, search } = useLocation();
  const location = useLocation();
  const params = new URLSearchParams(search);
  const redirectTo = params.get("redirect_to");
  const postText = params.get("postText");
  const redirectPath = params.get("redirect");
  const registerStore = useSelector((state) => state.auth);

  // Store postText and redirect path in localStorage if present
  useEffect(() => {
    if (postText) {
      localStorage.setItem("pendingPostText", postText);
    }
    if (redirectPath) {
      localStorage.setItem("pendingRedirect", redirectPath);
    }
  }, [postText, redirectPath]);
  const [otpVerified, setOtpVerified] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get auth state from Redux
  const {
    isLoading,
    isSuccess,
    isError,
    errorMessage,
    successMessage,
    user,
    token,
    actionFlag,
    newUser,
  } = useSelector((state) => state.auth);

  // React Hook Form setup with Yup validation
  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    // formState: { errors, touchedFields },
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
    mode: "onChange",
    defaultValues: {
      email: isDemoSite() ? DEMO_CREDENTIALS.email : "",
      password: isDemoSite() ? DEMO_CREDENTIALS.password : "",
    },
  });

  // Facebook login hook
  const {
    handleFacebookLogin,
    isLoading: isFacebookLoading,
  } = useFacebookLogin();

  // Apple login hook
  const { handleAppleLogin, isLoading: isAppleLoading } = useAppleLogin();

  // const onSubmit = async (data) => {
  //   // Dispatch login action with username field
  //   dispatch(loginUser({ username: data.email, password: data.password }));
  // };
  const onSubmit = async (data) => {
    const token = localStorage.getItem("authToken");

    if (rememberMe) {
      localStorage.setItem("rememberEmail", data.email);
    } else {
      localStorage.removeItem("rememberEmail");
      // localStorage.removeItem("rememberPassword");
    }

    const response = await dispatch(
      loginUser({ username: data.email, password: data.password, device_type: "web" }),
    );
    const userId = response.payload.data?.user_id;
    setPendingUser(response.payload.data?.user_id);

    // const token = localStorage.getItem("authToken")
    if (!token && userId) {
      setShowOtpModals(true);
    }
  };

  // Handle Facebook login button click
  const onFacebookLoginClick = async () => {
    try {
      const result = await handleFacebookLogin();
    } catch (error) {
      console.error("Facebook login error:", error);
      showError(error.message || "Facebook login failed. Please try again.");
    }
  };
  useEffect(() => {
    if (isDemoSite()) return;

    const savedEmail = localStorage.getItem("rememberEmail");
    const savedPassword = localStorage.getItem("rememberPassword");

    if (savedEmail && savedPassword) {
      setValue("email", savedEmail);
      setValue("password", savedPassword);
      setRememberMe(true);
    }
  }, [setValue]);

  const handleGoogleLogin = UseGoogleLogin(); // get login function from hook
  const {
    loginInsta: handleInstagramLogin,
    handleCallback,
  } = UseInstagramLogin();
  // console.log(handleCallback, handleInstagramLogin, "Hooks of data");
  const onInstaLoginClick = async () => {
    try {
      await handleInstagramLogin();
    } catch (error) {
      console.error("Instagram login error:", error);
      alert(error.message || "Instagram login failed. Please try again.");
    }
  };

  // // Handle redirect after Instagram callback
  useEffect(() => {
    handleCallback();
  }, [handleCallback]);
  const onGoogleLoginClick = async () => {
    try {
      await handleGoogleLogin(); // triggers the login popup
    } catch (error) {
      console.error("Google login error:", error);
      alert(error.message || "Google login failed. Please try again.");
    }
  };

  // Handle Apple login button click
  const onAppleLoginClick = async () => {
    try {
      await handleAppleLogin();
    } catch (error) {
      console.error("Apple login error:", error);
      showError(error.message || "Apple login failed. Please try again.");
    }
  };

  // useEffect(() => {
  //   const token = getToken();
  //   if (token && !isSuccess) {
  //     if (redirectTo) {
  //       navigate(redirectTo, {
  //         replace: true,
  //         state: location.state,
  //       });
  //     } else {
  //       navigate("/dashboard", { replace: true });
  //     }
  //   }
  // }, [navigate, redirectTo, isSuccess, location.state]);
  useEffect(() => {
    const token = getToken();

    if (token && !isSuccess) {
      let lastRoute = localStorage.getItem("lastRoute");

      // Helper function to check if a route is protected (inside auth)
      const isProtectedRoute = (path) => {
        if (!path) return false;
        
        const publicRoutes = [
          "/",
          "/login",
          "/registration",
          "/testimonial",
          "/salaries",
          "/comparisons",
          "/city-comparison",
          "/unsubscribe",
          "/poll/vote",
          "/poll/take",
        ];

        // Check exact match for public routes
        if (publicRoutes.includes(path)) return false;

        // Check for public dynamic routes
        if (path.startsWith("/share/")) return false;
        if (path.startsWith("/jobs/") && path.split("/").length === 3) return false; // /jobs/:slug
        // Poll pages always carry a ?token=... query string, so lastRoute is
        // saved as e.g. "/poll/vote?token=xyz" — the exact-match check above
        // only matches the bare "/poll/vote", so these need a prefix check too.
        if (path.startsWith("/poll/vote") || path.startsWith("/poll/take")) return false;

        // All other routes are protected
        return true;
      };

      // Convert /messaging/{userId} to /messaging
      if (lastRoute && lastRoute.match(/^\/messaging\/\d+$/)) {
        lastRoute = "/messaging";
      }

      if (redirectTo) {
        navigate(redirectTo, {
          replace: true,
          state: location.state,
        });
      } else if (lastRoute && isProtectedRoute(lastRoute)) {
        navigate(lastRoute, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [navigate, redirectTo, isSuccess, location.state]);

  // useEffect(() => {
  //   if (isSuccess && user && token) {
  //     showSuccess(successMessage || "Login successful!");
  //     dispatch(resetAuthState());

  //     const redirectToState = location.state?.redirectTo;

  //     if (redirectToState) {
  //       navigate(redirectToState, {
  //         replace: true,
  //         state: location.state, // ✅ forward EVERYTHING
  //       });
  //     } else if (redirectTo) {
  //       navigate(redirectTo, { replace: true });
  //     } else {
  //       navigate("/dashboard", { replace: true });
  //     }
  //   }

  //   if (isError && errorMessage) {
  //     showError(errorMessage);
  //     dispatch(resetAuthState());
  //   }
  // }, [
  //   isSuccess,
  //   isError,
  //   user,
  //   token,
  //   successMessage,
  //   errorMessage,
  //   navigate,
  //   dispatch,
  //   redirectTo,
  //   location.state?.from?.pathname,
  //   location.state?.from?.search,
  // ]);
  useEffect(() => {
    if (isSuccess && user && token) {
      showSuccess(successMessage || "Login successful!");
      dispatch(resetAuthState());

      const redirectToState = location.state?.redirectTo;

      // ✅ Check for pending redirect from URL params
      const pendingRedirect = localStorage.getItem("pendingRedirect");
      let lastRoute = localStorage.getItem("lastRoute");

      // Helper function to check if a route is protected (inside auth)
      const isProtectedRoute = (path) => {
        if (!path) return false;
        
        const publicRoutes = [
          "/",
          "/login",
          "/registration",
          "/testimonial",
          "/salaries",
          "/comparisons",
          "/city-comparison",
          "/unsubscribe",
          "/poll/vote",
          "/poll/take",
        ];

        // Check exact match for public routes
        if (publicRoutes.includes(path)) return false;

        // Check for public dynamic routes
        if (path.startsWith("/share/")) return false;
        if (path.startsWith("/jobs/") && path.split("/").length === 3) return false; // /jobs/:slug
        // Poll pages always carry a ?token=... query string, so lastRoute is
        // saved as e.g. "/poll/vote?token=xyz" — the exact-match check above
        // only matches the bare "/poll/vote", so these need a prefix check too.
        if (path.startsWith("/poll/vote") || path.startsWith("/poll/take")) return false;

        // All other routes are protected
        return true;
      };

      // Convert /messaging/{userId} to /messaging
      if (lastRoute && lastRoute.match(/^\/messaging\/\d+$/)) {
        lastRoute = "/messaging";
      }

      if (redirectToState) {
        navigate(redirectToState, {
          replace: true,
          state: location.state, // ✅ forward EVERYTHING
        });
      } else if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else if (pendingRedirect) {
        // ✅ Redirect to pending path (e.g., /community)
        navigate(pendingRedirect, { replace: true });
        localStorage.removeItem("pendingRedirect"); // Clean up
      } else if (lastRoute && isProtectedRoute(lastRoute)) {
        navigate(lastRoute, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }

    if (isError && errorMessage) {
      showError(errorMessage);
      dispatch(resetAuthState());
    }
  }, [
    isSuccess,
    isError,
    user,
    token,
    successMessage,
    errorMessage,
    navigate,
    dispatch,
    redirectTo,
    location.state?.from?.pathname,
    location.state?.from?.search,
  ]);

  useEffect(() => {
    if (isSuccess && actionFlag) {
      if (actionFlag === "SOCIAL_LOGIN_SUCCESS") {
        showSuccess("Welcome back!");
        dispatch(resetAuthState());

        const fromState = location.state?.from?.pathname;
        const fromSearch = location.state?.from?.search || "";

        if (fromState) {
          navigate(`${fromState}${fromSearch}`, { replace: true });
        } else if (redirectTo) {
          navigate(redirectTo, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else if (actionFlag === "SOCIAL_SIGNUP_SUCCESS") {
        showSuccess(successMessage || "Please complete your profile");
        dispatch(resetAuthState());
        navigate("/registration", {
          state: { fromSocialLogin: true, user: newUser },
        });
      }
    }
  }, [
    isSuccess,
    actionFlag,
    successMessage,
    navigate,
    dispatch,
    redirectTo,
    newUser,
    location.state?.from?.pathname,
    location.state?.from?.search,
  ]);
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 0);
      }
    }
  }, [hash]);

  // const handleLogin = () => {
  //   // your login logic

  //   if (redirectTo) {
  //     navigate(redirectTo);
  //   } else {
  //     navigate("/dashboard");
  //   }
  // };

  const onOtpVerified = async () => {
    // Get token and user data from Redux store (saved by verifyOtpLoginSignup)
    const token = registerStore?.token;

    // Token and user data should already be saved to localStorage by the Redux action
    // But we'll verify and log it here
    if (token) {
    } else {
      console.warn("⚠️ No token found in store after OTP verification");
    }

    setOtpVerified(true);
    setShowOtpModal(false);
    // setIsAuthenticated(true);

    try {
      await Swal.fire({
        icon: "success",
        title: "OTP Verified",
        text: "Your phone/email has been verified. Lets complete your profile!",
        timer: 1800,
        showConfirmButton: false,
        customClass: {
          popup: "success-popup-modal",
        },
      });
    } catch (e) {
      console.error("Swal error:", e);
    }
  };

  return (
    <>
      <SEO
        title={seoConfig.login.title}
        description={seoConfig.login.description}
        keywords={seoConfig.login.keywords}
      />
      <section className="login-content with-login">
        <Container>
          <div className="title-subtitle text-center" id="login">
            <h3 className="title">Take Back Control of Your Nursing Career</h3>
            <div className="description">
              {/* Simple, <b>transparent,</b> and <b>affordable plans</b> created to
              grow with your business. */}
              Log in to unlock real salary data, cost-of-living breakdowns, and
              financial tools built for nurses.
            </div>
          </div>

          <div className="login-content-row">
            <Row>
              <Col lg={6} className="social-login-content">
                <Card>
                  <Card.Body>
                    <div className="logo-imgs">
                      {resolvedTheme === "dark" ? <LogoDark /> : <Logo />}
                    </div>
                    <div className="content text-center">
                      <p>Please fill this form to login your account</p>
                    </div>
                    <div className="social-login">
                      <Button
                        className="social facebook"
                        onClick={onFacebookLoginClick}
                        disabled={isLoading || isFacebookLoading}
                      >
                        <div className="imgs">
                          <Facebook />
                        </div>
                        <div className="text">
                          {isFacebookLoading
                            ? "Connecting..."
                            : "Sign in with Facebook"}
                        </div>
                      </Button>
                      <Button
                        className="social google"
                        onClick={onGoogleLoginClick}
                        disabled={isLoading || isFacebookLoading}
                      >
                        <div className="imgs">
                          <Google />
                        </div>
                        <div className="text">Sign in with google+</div>
                      </Button>
                       {/*<Button
                        className="social instagram"
                        onClick={() => onInstaLoginClick()}
                        disabled={isLoading || isFacebookLoading}
                      >
                        <div className="imgs">
                          <Instagram />
                        </div>
                        <div className="text">Sign in with instagram</div>
                      </Button> */}
                      <Button
                        className="social apple"
                        onClick={onAppleLoginClick}
                        disabled={isLoading || isAppleLoading}
                      >
                        <div className="imgs">
                          <AppleIcon />
                        </div>
                        <div className="text">
                          {isAppleLoading
                            ? "Connecting..."
                            : "Sign in with Apple"}
                        </div>
                      </Button>
                    </div>
                    <div className="content text-center pt-2 pb-1">
                      <p>Download our app for the best experience.</p>
                    </div>
                    <div className="app-icon text-center">
                      <a
                        href="https://apps.apple.com/us/app/map-my-pay/id6747190135"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download on App Store"
                      >
                        {resolvedTheme === "dark" ? <AppIconDark /> : <AppIcon />}
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.mapmypay"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Get it on Google Play"
                      >
                        {resolvedTheme === "dark" ? (
                          <AndroindIconDark />
                        ) : (
                          <AndroindIcon />
                        )}
                      </a>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6} className="login">
                <Card>
                  <Card.Body>
                    <div className="content text-center">
                      <h5>Welcome back</h5>
                      <p>Please fill this form to login your account</p>
                    </div>
                    <Form onSubmit={handleFormSubmit(onSubmit)}>
                      <Form.Group className="form-group" controlId="email">
                        <Form.Label className="col-label">
                          Email address
                        </Form.Label>
                        <Form.Control
                          type="email"
                          {...register("email")}
                          placeholder="Enter email address"
                          className="col-input"
                          isInvalid={!!errors.email}
                          readOnly={isDemoSite()}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="form-group" controlId="password">
                        <Form.Label className="col-label">Password</Form.Label>
                        <InputGroup hasValidation>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            {...register("password")}
                            placeholder="************"
                            className="col-input"
                            isInvalid={!!errors.password}
                            readOnly={isDemoSite()}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label="Toggle password visibility"
                          >
                            {showPassword ? (
                              <Eye size={20} />
                            ) : (
                              <EyeOff size={20} />
                            )}
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            {errors.password?.message}
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                      <div className="form-group d-flex align-items-center justify-content-between">
                        <div className="checkbox-content">
                          <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                          />
                          <label
                            htmlFor="rememberMe"
                            className="cursor-pointer"
                          >
                            Remember Me
                          </label>
                        </div>

                        <Nav.Link
                          as="span"
                          className="forget-link"
                          onClick={() => setShowOtpModal(true)}
                          style={{ cursor: "pointer" }}
                        >
                          Forgot password?
                        </Nav.Link>
                      </div>
                      <div className="buttons">
                        <Button
                          type="submit"
                          variant="primary"
                          className="w-100"
                          disabled={isLoading}
                        >
                          {isLoading ? "Logging in..." : "Login"}
                        </Button>
                      </div>
                      <div className="signup-content">
                        Don't have an account?{" "}
                        <Nav.Link as={NavLink} to="/registration">
                          Sign up
                        </Nav.Link>
                      </div>
                    </Form>
                    <OtpModal
                      show={showOtpModal}
                      handleClose={() => setShowOtpModal(false)}
                    />

                    <VerifyOtpModal
                      show={showOtpModals}
                      handleClose={() => setShowOtpModals(false)}
                      onOtpVerified={onOtpVerified}
                      createdUserId={pendingUser}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </>
  );
};

export default Login;
